import apiService from './api';
import type { ApiResponse } from '../types';
import { openDocument } from '../utils/openDocument';

/**
 * staffCertification — API service for the unified staff certifications /
 * licenses register (forklift, first aid, truck licence, ACUP, etc.).
 *
 * Mirrors the shape of sprayCredential.ts but targets the generalised
 * /api/certifications endpoints. All write calls that may carry a document use
 * FormData (multipart/form-data); axios sets the boundary automatically.
 *
 * Access model (enforced server-side):
 *   - Self-service (any authenticated org member): read the active type catalog
 *     and manage their OWN certifications.
 *   - HR management (timesheet.hr license seat): manage types/categories, view
 *     and edit any user's records, and read the org-wide report.
 */

export type CertificationStatus = 'valid' | 'expiring_soon' | 'expired' | 'missing';

/** A `module` filter value accepted by the type/category/report endpoints. */
export type CertificationModuleFilter = 'any' | 'general' | (string & {});

export interface StaffCertificationType {
  id: number;
  organisation_id: number;
  name: string;
  category: string | null;
  description: string | null;
  module_key: string | null;
  is_required: boolean;
  required_for: string | null;
  is_active: boolean;
  created_at: string;
  created_by: number | null;
}

export interface UserCertification {
  id: number;
  user_id: number;
  organisation_id: number;
  certification_type_id: number;
  credential_number: string | null;
  issuer: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  document_original_name: string | null;
  document_mime_type: string | null;
  has_document: boolean;
  notes: string | null;
  created_at: string;
  created_by: number | null;
  updated_at: string;
  type_name: string;
  type_category: string | null;
  module_key: string | null;
  type_is_required: boolean;
  status: Exclude<CertificationStatus, 'missing'>;
}

/** One row per (user, type) from the org-wide report. `status` may be 'missing'. */
export interface CertificationReportRow {
  user_id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  certification_type_id: number;
  type_name: string;
  type_category: string | null;
  module_key: string | null;
  type_is_required: boolean;
  certification_id: number | null;
  credential_number: string | null;
  issuer: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  document_original_name: string | null;
  has_document: boolean;
  status: CertificationStatus;
}

// ---------------------------------------------------------------------------
// Certification types (org catalog)
// ---------------------------------------------------------------------------

export async function listCertificationTypes(opts: {
  module?: CertificationModuleFilter;
  includeInactive?: boolean;
  category?: string;
} = {}): Promise<ApiResponse<StaffCertificationType[]>> {
  const params = new URLSearchParams();
  if (opts.module) params.set('module', opts.module);
  if (opts.includeInactive) params.set('include_inactive', 'true');
  if (opts.category) params.set('category', opts.category);
  const q = params.toString();
  return apiService.get<StaffCertificationType[]>(`/certifications/types${q ? `?${q}` : ''}`);
}

export async function listCertificationCategories(
  module: CertificationModuleFilter = 'any',
): Promise<ApiResponse<string[]>> {
  return apiService.get<string[]>(`/certifications/categories?module=${encodeURIComponent(module)}`);
}

export async function createCertificationType(data: {
  name: string;
  category?: string | null;
  description?: string | null;
  is_required?: boolean;
  required_for?: string | null;
}): Promise<ApiResponse<StaffCertificationType>> {
  return apiService.post<StaffCertificationType>('/certifications/types', data);
}

export async function updateCertificationType(
  id: number,
  data: Partial<Pick<StaffCertificationType,
    'name' | 'category' | 'description' | 'is_required' | 'required_for' | 'is_active'>>,
): Promise<ApiResponse<StaffCertificationType>> {
  return apiService.put<StaffCertificationType>(`/certifications/types/${id}`, data);
}

export async function deactivateCertificationType(id: number): Promise<ApiResponse<unknown>> {
  return apiService.delete<unknown>(`/certifications/types/${id}`);
}

// ---------------------------------------------------------------------------
// Certification records
// ---------------------------------------------------------------------------

export async function listMyCertifications(): Promise<ApiResponse<UserCertification[]>> {
  return apiService.get<UserCertification[]>('/certifications/records/me');
}

export async function listUserCertifications(userId: number): Promise<ApiResponse<UserCertification[]>> {
  return apiService.get<UserCertification[]>(`/certifications/records/user/${userId}`);
}

export async function getCertificationReport(
  module: CertificationModuleFilter = 'any',
): Promise<ApiResponse<CertificationReportRow[]>> {
  return apiService.get<CertificationReportRow[]>(
    `/certifications/records/report?module=${encodeURIComponent(module)}`,
  );
}

/**
 * Create a certification. Uses FormData so an optional `document` file can be
 * attached in the same request. Pass `user_id` (HR only) to record on another
 * user's behalf.
 */
export async function createCertification(args: {
  certification_type_id: number;
  credential_number?: string;
  issuer?: string;
  issued_date?: string;
  expiry_date?: string;
  notes?: string;
  document?: File | null;
  user_id?: number;
}): Promise<ApiResponse<UserCertification>> {
  const form = new FormData();
  form.append('certification_type_id', String(args.certification_type_id));
  if (args.credential_number) form.append('credential_number', args.credential_number);
  if (args.issuer) form.append('issuer', args.issuer);
  if (args.issued_date) form.append('issued_date', args.issued_date);
  if (args.expiry_date) form.append('expiry_date', args.expiry_date);
  if (args.notes) form.append('notes', args.notes);
  if (args.user_id) form.append('user_id', String(args.user_id));
  if (args.document) form.append('document', args.document);
  return apiService.post<UserCertification>('/certifications/records', form);
}

export async function updateCertification(
  id: number,
  args: {
    credential_number?: string;
    issuer?: string;
    issued_date?: string;
    expiry_date?: string;
    notes?: string;
    document?: File | null;
  },
): Promise<ApiResponse<UserCertification>> {
  const form = new FormData();
  if (args.credential_number !== undefined) form.append('credential_number', args.credential_number);
  if (args.issuer !== undefined) form.append('issuer', args.issuer);
  if (args.issued_date !== undefined) form.append('issued_date', args.issued_date);
  if (args.expiry_date !== undefined) form.append('expiry_date', args.expiry_date);
  if (args.notes !== undefined) form.append('notes', args.notes);
  if (args.document) form.append('document', args.document);
  return apiService.put<UserCertification>(`/certifications/records/${id}`, form);
}

export async function deleteCertification(id: number): Promise<ApiResponse<unknown>> {
  return apiService.delete<unknown>(`/certifications/records/${id}`);
}

/** A record's attached document. The route needs the bearer token, so it cannot be a plain href. */
export async function openCertificationDocument(c: Pick<UserCertification, 'id' | 'document_original_name'>): Promise<void> {
  await openDocument(`/certifications/records/${c.id}/document`, c.document_original_name || `certification-${c.id}`);
}
